import {
  OnChangeFn,
  RowSelectionState,
  VisibilityState,
} from '@tanstack/react-table';
import produce from 'immer';
import _ from 'lodash';
import {useMemo} from 'react';
import {create, StoreApi, useStore} from 'zustand';
import {persist, devtools} from 'zustand/middleware';
import {ObjectId} from 'bson';
import {AccountQuery, AccountSort} from '../../shared/api/api';
import {
  AccountView,
  createAccountFilter,
} from '../../shared/filter/accountFilter';
import {track} from '../../analytics';
import {keys} from '../../shared/util';
import {useAccountView} from './AccountViewContext';
import {createFilterStore, FilterState} from './filterStore';
import {filterItemsToConditions} from './filterTypes';

export interface GridState {
  filterStore: StoreApi<FilterState>;
  gridView: AccountView;

  sort: AccountSort;
  setSort: (sort: AccountSort) => void;

  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;

  rowSelection: RowSelectionState;
  setRowSelection: OnChangeFn<RowSelectionState>;

  numBulkSelected: number;
  setNumBulkSelected: (selected: number) => void;

  getNumberOfSelectedAccounts: () => number;
  resetRowSelection: () => void;
}

type TopLevelGridState = {
  [view in AccountView]: GridState;
};

const getDefaultSort = (view: AccountView): AccountSort => {
  if (view === 'saved') {
    return {field: 'saveDate', order: 'desc'};
  }
  if (view === 'intent') {
    return {field: 'intentDate', order: 'desc'};
  }
  return {field: 'overallFit', order: 'desc'};
};

export const useTopLevelGridStore = create<TopLevelGridState>()(
  devtools(
    persist<TopLevelGridState>(
      (set, get) => {
        const createGridViewState = (gridView: AccountView): GridState => ({
          filterStore: createFilterStore(),
          gridView: gridView,

          sort: getDefaultSort(gridView),
          setSort(sort) {
            set(
              produce<TopLevelGridState>((state) => {
                track('accountsSorted', {properties: {sort}});
                state[gridView].sort = sort;
                _resetRowSelection(state, gridView);
              })
            );
          },

          columnVisibility: {
            locations: false,
            industry: false,
            tagline: false,
          },
          setColumnVisibility(newColumnVisibility) {
            set(
              produce<TopLevelGridState>((state) => {
                if (typeof newColumnVisibility === 'function') {
                  state[gridView].columnVisibility = newColumnVisibility(
                    state[gridView].columnVisibility
                  );
                } else {
                  state[gridView].columnVisibility = newColumnVisibility;
                }
                track('accountsViewColumnsUpdated', {
                  properties: {columns: state[gridView].columnVisibility},
                });
              })
            );
          },

          rowSelection: {},
          setRowSelection(newRowSelection) {
            set(
              produce<TopLevelGridState>((state) => {
                if (typeof newRowSelection === 'function') {
                  state[gridView].rowSelection = newRowSelection(
                    state[gridView].rowSelection
                  );
                } else {
                  state[gridView].rowSelection = newRowSelection;
                }
              })
            );
          },

          numBulkSelected: 0,
          setNumBulkSelected(selected) {
            set(
              produce<TopLevelGridState>((state) => {
                state[gridView].numBulkSelected = selected;
                if (!selected) {
                  state[gridView].rowSelection = {};
                }
              })
            );
          },

          getNumberOfSelectedAccounts() {
            const state = get()[gridView];
            return state.numBulkSelected
              ? state.numBulkSelected
              : Object.values(state.rowSelection).filter(_.identity).length;
          },

          resetRowSelection() {
            set(
              produce<TopLevelGridState>((state) => {
                _resetRowSelection(state, gridView);
              })
            );
          },
        });

        return {
          all: createGridViewState('all'),
          recommended: createGridViewState('recommended'),
          imported: createGridViewState('imported'),
          saved: createGridViewState('saved'),
          crm: createGridViewState('crm'),
          intent: createGridViewState('intent'),
        };
      },
      {
        name: 'account-grid-controlstate',
        version: 20250215,

        // Only persist columnVisibility from localStorage.
        // Use shallow clone (not _.merge) to preserve filterStore references.
        merge: (persistedState, currentState) => {
          const ps = persistedState as Partial<TopLevelGridState>;
          const out = {...currentState};
          for (const viewKey of keys(out)) {
            out[viewKey] = {...currentState[viewKey]};
            const persisted = ps[viewKey];
            if (persisted?.columnVisibility) {
              out[viewKey].columnVisibility = persisted.columnVisibility;
            }
          }
          return out;
        },
      }
    ),
    {enabled: import.meta.env.DEV}
  )
);

export function useGridStore(): GridState {
  const view = useAccountView();
  return useTopLevelGridStore((s) => s[view]);
}

const _resetRowSelection = (
  state: TopLevelGridState,
  gridView: AccountView
) => {
  state[gridView].rowSelection = {};
  state[gridView].numBulkSelected = 0;
};

// Creates the query parameters to send to the server
export const useAccountQueryParams = (runId?: ObjectId): AccountQuery => {
  const view = useAccountView();
  const {sort, filterStore} = useGridStore();
  const {filterItems, filterOperator, search, disqualified, excluded} =
    useStore(filterStore);

  return useMemo(() => {
    const {items, operator} = filterItemsToConditions(
      filterItems,
      filterOperator
    );

    const filter = createAccountFilter(items, {
      operator,
      view,
      search,
      disqualified,
      excluded,
    });

    return {
      sort,
      filter,
      ...(runId ? {runId} : {}),
    } satisfies AccountQuery;
  }, [
    runId,
    view,
    sort,
    filterItems,
    filterOperator,
    search,
    disqualified,
    excluded,
  ]);
};
